import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PLineItem } from './useUserLineItemsPagination';
import { useClient } from '@web/api';
import { getItemDataForLineItem } from '@/components/InventoryCarousel/getItemDataForLineItem';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import store from '@/store/store';
import { verifySiloExists } from '@/utils/verifySiloExists';
import { Cart, CartDeliveryAddress } from '@agrantis/cart';
import { addCartItem, clearCart } from '@agrantis/cart/actions/cart';
import { selectCartByKey } from '@agrantis/cart/selectors';

const getFirstDelivery = (
  cart: Cart | null,
  siloId?: string | null,
): string | undefined => {
  const deliveries = cart?.deliveries || {};
  const deliveryIds = Object.keys(deliveries);
  const hasDeliveries = deliveryIds.length > 0;
  const isDeliveryWithSilo = deliveries[deliveryIds[0]]?.siloId;
  const shouldAddToExistingDelivery =
    hasDeliveries && !siloId && !isDeliveryWithSilo;

  return shouldAddToExistingDelivery
    ? deliveries[deliveryIds[0]].id
    : undefined;
};

const useOrderAgain = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [pendingLineItem, setPendingLineItem] = useState<PLineItem | null>(
    null,
  );

  const { t } = useTranslation();
  const navigate = useNavigate();
  const client = useClient();
  const retailer = useAppSelector((state) => state.retailer.retailer);
  const dispatch = useAppDispatch();

  const processOrderAgain = useCallback(
    async (lineItem: PLineItem) => {
      if (!retailer?.id) return;
      setIsLoading(true);

      const {
        delivery: oldDelivery,
        itemData,
        priceData,
        shouldIncludeComments,
      } = await getItemDataForLineItem(client, lineItem);

      const siloExists = await verifySiloExists(oldDelivery?.siloID, client);

      const canReorder =
        !!lineItem.inventory?.articleData?.id &&
        !!lineItem.delivery?.type &&
        siloExists;

      if (itemData == null || !priceData || !canReorder) {
        alert(siloExists ? 'Artikel nicht gefunden' : 'Silo nicht gefunden');
        setIsLoading(false);
        return;
      }

      if (!oldDelivery) {
        alert('Lieferung nicht gefunden');
        setIsLoading(false);
        return;
      }

      const price = priceData.finalPrice;
      const deliveryAddress: CartDeliveryAddress = {
        firstName: oldDelivery.deliveryAddress?.firstName ?? '',
        lastName: oldDelivery.deliveryAddress?.lastName ?? '',
        addressLine1: oldDelivery.deliveryAddress?.addressLine1 ?? '',
        addressLine2: oldDelivery.deliveryAddress?.addressLine2 ?? '',
        addressLocality: oldDelivery.deliveryAddress?.addressLocality ?? '',
        addressPostcode: oldDelivery.deliveryAddress?.addressPostcode ?? '',
        addressDistrict: oldDelivery.deliveryAddress?.addressDistrict ?? '',
        country: oldDelivery.deliveryAddress?.country ?? '',
        company: oldDelivery.deliveryAddress?.company ?? '',
      };

      const currentState = store.getState();
      const currentCart = selectCartByKey(retailer.id)(currentState);
      const firstDeliveryId = getFirstDelivery(
        currentCart,
        oldDelivery?.siloID,
      );

      try {
        dispatch(
          addCartItem({
            cartKey: lineItem.retailerID,
            locationId: oldDelivery.locationID
              ? parseInt(oldDelivery.locationID)
              : undefined,
            deliveryId: firstDeliveryId,
            delivery: !firstDeliveryId
              ? {
                  isReOrder: true,
                  type: oldDelivery.type,
                  deliveryAddress,
                  siloId: oldDelivery.siloID
                    ? parseInt(oldDelivery.siloID)
                    : undefined,
                  comment: shouldIncludeComments
                    ? oldDelivery.comments ?? ''
                    : undefined,
                }
              : undefined,
            itemData,
            price: price ?? 0,
            amount: lineItem.quantity ?? 0,
            requiredDeliveryDate: retailer?.settings.requiredDeliveryDate,
          }),
        );
        navigate(`/${retailer.id}/checkout`);
      } catch (error) {
        console.error('Error while adding item to cart', error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      client,
      dispatch,
      navigate,
      retailer?.id,
      retailer?.settings.requiredDeliveryDate,
    ],
  );

  const handleOrderAgain = useCallback(
    (lineItem: PLineItem) => {
      if (!lineItem || !retailer?.id) return;

      if (!lineItem.inventory?.visibleForCustomers) {
        alert(t('inventory.reorder_warning_not_visible'));
        setIsLoading(false);
        return;
      }

      const currentState = store.getState();
      const currentCart = selectCartByKey(retailer.id)(currentState);
      const hasItems = currentCart
        ? Object.keys(currentCart.deliveries).length > 0
        : false;

      if (hasItems) {
        setPendingLineItem(lineItem);
        setWarningModalOpen(true);
      } else {
        processOrderAgain(lineItem);
      }
    },
    [retailer?.id, t, processOrderAgain],
  );

  const handleSubmitWarningModal = useCallback(async () => {
    if (!pendingLineItem || !retailer?.id) return;
    await dispatch(clearCart({ cartKey: retailer.id }));
    processOrderAgain(pendingLineItem);
    setWarningModalOpen(false);
  }, [dispatch, retailer?.id, pendingLineItem, processOrderAgain]);

  const handleCancelWarningModal = useCallback(() => {
    if (pendingLineItem) {
      processOrderAgain(pendingLineItem);
    }
    setWarningModalOpen(false);
  }, [pendingLineItem, processOrderAgain]);

  return {
    handleOrderAgain,
    isLoading,
    warningModalOpen,
    setWarningModalOpen,
    handleSubmitWarningModal,
    handleCancelWarningModal,
  };
};

export default useOrderAgain;
